/**
 * The GST tax engine (spec §4) — the single most critical component in the
 * system. Pure functions, plain objects in, plain objects out. No imports from
 * Express, Prisma or React, so the API and the SPA run byte-identical maths and
 * the live preview can never disagree with the persisted invoice (spec §2.1).
 */
import { amountInWords } from "./amount-in-words.js";
import { apportionByWeights } from "./apportion.js";
import { assertInteger, assertNonNegativeInteger, divRoundHalfUp, mulExact, roundToNearestRupeePaise, } from "./rounding.js";
/** Milli-units per whole unit — qty 1.000 is stored as 1000. */
const MILLI = 1000;
/** Grams per kilogram — `BY_WEIGHT` rates are per kg. */
const GRAMS_PER_KG = 1000;
/** Millimetres per metre — `BY_LENGTH` rates are per metre. */
const MM_PER_METRE = 1000;
/**
 * CGST (and SGST) is half the GST rate:
 *   taxable × (bp / 2) / 10000  ===  taxable × bp / 20000
 * Done in one division so the half-rate never has to be rounded twice.
 */
const CGST_SGST_DIVISOR = 20_000;
/** IGST is the full rate: taxable × bp / 10000. */
const IGST_DIVISOR = 10_000;
const PRICING_MODES = ["LUMPSUM", "BY_PIECE", "BY_WEIGHT", "BY_LENGTH"];
/* -------------------------------------------------------------------------- */
/* §15(3)(a) — "a discount may not exceed what it discounts", as DATA           */
/* -------------------------------------------------------------------------- */
/**
 * The marker the {@link isDiscountRefusal} guard reads.
 *
 * A PROPERTY RATHER THAN `instanceof`, deliberately. This package is imported by
 * the API and by the SPA, is built once and bundled again, and `instanceof`
 * across two copies of the same class silently answers false — which would fall
 * back to printing the engineer's sentence, the exact defect this type exists to
 * fix, and only in the bundled build where nobody is looking.
 */
const DISCOUNT_REFUSAL = "steelbill.tax.discountExceedsValue";
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THE REFUSAL CARRIES FIGURES AND NOT JUST A SENTENCE.
 *
 * The engine's `Error.message` is written for an ENGINEER and always has been —
 * raw paise, no grouping, no ₹, and the line named by the CLIENT-GENERATED key
 * ("L3"), which appears nowhere on the owner's screen. `apps/app/src/lib/totals.ts`
 * states the contract outright: engine messages are "NEVER SHOWN TO THE OWNER …
 * §11.1 requires plain language."
 *
 * But the API surfaces exactly this refusal as a 400 whose message the totals bar
 * prints verbatim in red, so for the two DISCOUNT refusals — the most likely
 * mistake with the feature — that contract was being broken. Rewording the
 * message here would only move the problem: this file cannot know a line's SR.NO
 * (`10-10`), which is derived when the document is written, so the sentence the
 * owner needs cannot be composed from inside the engine.
 *
 * So the refusal carries the FACTS and the caller composes the sentence:
 * `apps/api/src/services/documents.ts` formats the money through this package's
 * own `formatRupees` (Indian grouping — CLAUDE.md makes that correctness) and
 * names the line by the code the owner can actually see. The `message` stays
 * engineer-facing and byte-identical to what it was, so anything reading it —
 * the console, a log line, the existing engine tests — is unchanged.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export class DiscountExceedsValueError extends Error {
    /** Read by {@link isDiscountRefusal}; see the note on {@link DISCOUNT_REFUSAL}. */
    steelbillErrorKind = DISCOUNT_REFUSAL;
    /** Which of the two discounts (§15(3)(a)) was refused. */
    scope;
    /** The offending line's key, or `null` for the whole-document discount. */
    key;
    /** What was asked for, in paise. */
    discountPaise;
    /** What there was to discount, in paise — for a document, net of line discounts. */
    availablePaise;
    constructor(detail) {
        super(detail.message);
        this.name = "DiscountExceedsValueError";
        this.scope = detail.scope;
        this.key = detail.key;
        this.discountPaise = detail.discountPaise;
        this.availablePaise = detail.availablePaise;
    }
}
/** Is this thrown value the engine refusing a discount larger than its base? */
export function isDiscountRefusal(error) {
    return (typeof error === "object" &&
        error !== null &&
        error.steelbillErrorKind === DISCOUNT_REFUSAL);
}
/**
 * Normalises a 2-digit Indian state code.
 *
 * Accepts surrounding whitespace and a single digit (`"7"` → `"07"`), because
 * state codes arrive from GSTIN substrings, dropdowns and hand entry alike.
 *
 * @throws when the value is not a 1- or 2-digit numeric string.
 */
export function normaliseStateCode(value, label) {
    if (typeof value !== "string") {
        throw new Error(`${label} must be a 2-digit state code string, received ${String(value)}`);
    }
    const trimmed = value.trim();
    const padded = /^\d$/.test(trimmed) ? `0${trimmed}` : trimmed;
    if (!/^\d{2}$/.test(padded)) {
        throw new Error(`${label} must be a 2-digit state code such as "27", received "${value}"`);
    }
    return padded;
}
/**
 * Intra-state when the place of supply matches the company's state, otherwise
 * inter-state (spec §4).
 *
 * The caller supplies the customer's state code: the first 2 chars of the
 * customer GSTIN when registered, otherwise the state selected on the document
 * for an unregistered/B2C buyer.
 */
export function determineSupplyType(companyStateCode, customerStateCode) {
    const company = normaliseStateCode(companyStateCode, "companyStateCode");
    const customer = normaliseStateCode(customerStateCode, "customerStateCode");
    return company === customer ? "INTRA_STATE" : "INTER_STATE";
}
function requireDimension(value, field, mode, key) {
    if (value === null || value === undefined) {
        throw new Error(`Line "${key}" uses pricingMode ${mode} and therefore requires ${field}`);
    }
    assertNonNegativeInteger(value, `Line "${key}" ${field}`);
    return value;
}
/** Validates the fields every line must carry, header or not. */
function validateCommonFields(line) {
    if (!PRICING_MODES.includes(line.pricingMode)) {
        throw new Error(`Line "${line.key}" has unknown pricingMode ${JSON.stringify(line.pricingMode)}; expected one of ${PRICING_MODES.join(", ")}`);
    }
    assertNonNegativeInteger(line.gstRateBp, `Line "${line.key}" gstRateBp`);
    assertNonNegativeInteger(line.qtyMilli, `Line "${line.key}" qtyMilli`);
    assertNonNegativeInteger(line.ratePaise, `Line "${line.key}" ratePaise`);
    // Optional dimensions are validated whenever they are present, regardless of
    // the pricing mode that will actually consume them.
    if (line.pieces !== null && line.pieces !== undefined) {
        assertNonNegativeInteger(line.pieces, `Line "${line.key}" pieces`);
    }
    if (line.weightGrams !== null && line.weightGrams !== undefined) {
        assertNonNegativeInteger(line.weightGrams, `Line "${line.key}" weightGrams`);
    }
    if (line.lengthMm !== null && line.lengthMm !== undefined) {
        assertNonNegativeInteger(line.lengthMm, `Line "${line.key}" lengthMm`);
    }
    if (line.discountPaise !== null && line.discountPaise !== undefined) {
        assertNonNegativeInteger(line.discountPaise, `Line "${line.key}" discountPaise`);
    }
}
/**
 * Reads the document-level discount out of the options bag.
 *
 * `undefined`, `null` and `0` all mean "no discount" and are collapsed to 0
 * here, so the apportionment pass can be skipped wholesale rather than run with
 * a zero that would thread new branches through code that has been producing
 * accepted invoices.
 */
function documentDiscountFrom(options) {
    const raw = options?.documentDiscountPaise;
    if (raw === null || raw === undefined) {
        return 0;
    }
    assertNonNegativeInteger(raw, "documentDiscountPaise");
    return raw;
}
/**
 * Taxable value of one billable line, by pricing mode (spec §4).
 *
 * Never called for group headers — they contribute nothing, so their pricing
 * inputs are irrelevant.
 */
function taxableFor(line) {
    switch (line.pricingMode) {
        case "LUMPSUM":
            // rate × qty, where qty is stored ×1000
            return divRoundHalfUp(mulExact(line.ratePaise, line.qtyMilli, `Line "${line.key}" LUMPSUM`), MILLI);
        case "BY_PIECE": {
            const pieces = requireDimension(line.pieces, "pieces", "BY_PIECE", line.key);
            return mulExact(pieces, line.ratePaise, `Line "${line.key}" BY_PIECE`);
        }
        case "BY_WEIGHT": {
            // ratePaise is per kg; weight is grams
            const grams = requireDimension(line.weightGrams, "weightGrams", "BY_WEIGHT", line.key);
            return divRoundHalfUp(mulExact(grams, line.ratePaise, `Line "${line.key}" BY_WEIGHT`), GRAMS_PER_KG);
        }
        case "BY_LENGTH": {
            // ratePaise is per metre; length is millimetres
            const lengthMm = requireDimension(line.lengthMm, "lengthMm", "BY_LENGTH", line.key);
            return divRoundHalfUp(mulExact(lengthMm, line.ratePaise, `Line "${line.key}" BY_LENGTH`), MM_PER_METRE);
        }
        default: {
            const exhaustive = line.pricingMode;
            throw new Error(`Line "${line.key}" has unknown pricingMode ${String(exhaustive)}`);
        }
    }
}
/**
 * Computes every line and the document totals.
 *
 * This is the server-authority calculation (spec §6): the client may send lines
 * and quantities, the server always recomputes taxable values, taxes, rounding,
 * totals and supply type.
 *
 * Discounts (§15(3)(a) CGST) reduce the TAXABLE VALUE, so tax is charged on the
 * reduced figure — see `EngineLineInput.discountPaise` for the per-line kind and
 * `ComputeOptions.documentDiscountPaise` for the whole-document kind. The scope
 * is invoices and quotations: a challan declares the value of goods in motion
 * rather than a negotiated price, and a credit note is already a reduction, so
 * discounting one is incoherent. Neither should ever be handed these fields.
 * (Post-supply discounts under §15(3)(b) need a prior agreement and ITC
 * reversal by the recipient — that is the credit-note path, not this one.)
 *
 * @throws a descriptive Error on any structurally invalid document — duplicate
 *   keys, a child pointing at a missing or non-header parent, a header that is
 *   itself a child, a missing pieces/weight/length for the pricing mode, a
 *   negative/non-integer amount, a discount on a group header, or a discount
 *   that exceeds what it discounts.
 */
export function computeTotals(lines, ctx, options) {
    if (!Array.isArray(lines)) {
        throw new Error("computeTotals: lines must be an array");
    }
    const supplyType = determineSupplyType(ctx.companyStateCode, ctx.customerStateCode);
    const documentDiscountPaise = documentDiscountFrom(options);
    // ---- pass 1: index by key, rejecting duplicates -------------------------
    const byKey = new Map();
    for (const line of lines) {
        if (line === null || typeof line !== "object") {
            throw new Error("computeTotals: every line must be an object");
        }
        if (typeof line.key !== "string" || line.key.trim() === "") {
            throw new Error(`computeTotals: every line needs a non-empty string key, received ${JSON.stringify(line.key)}`);
        }
        if (byKey.has(line.key)) {
            throw new Error(`computeTotals: duplicate line key "${line.key}"`);
        }
        byKey.set(line.key, line);
    }
    // ---- pass 2: structural + numeric validation ----------------------------
    for (const line of lines) {
        const isHeader = line.isGroupHeader === true;
        const parentKey = line.parentKey ?? null;
        if (isHeader && parentKey !== null) {
            throw new Error(`Line "${line.key}" is a group header and cannot also be a child of "${parentKey}" — the document hierarchy is only two levels deep`);
        }
        if (isHeader && (line.discountPaise ?? 0) !== 0) {
            throw new Error(`Line "${line.key}" is a group header and cannot carry a discount — a header contributes no value to discount; put the discount on its children`);
        }
        if (parentKey !== null) {
            if (parentKey === line.key) {
                throw new Error(`Line "${line.key}" cannot be its own parent`);
            }
            const parent = byKey.get(parentKey);
            if (parent === undefined) {
                throw new Error(`Line "${line.key}" references parent "${parentKey}" which does not exist in this document`);
            }
            if (parent.isGroupHeader !== true) {
                throw new Error(`Line "${line.key}" references parent "${parentKey}" which is not a group header — only group headers may have children`);
            }
        }
        validateCommonFields(line);
    }
    // ---- pass 3: taxable per billable line, net of its own line discount ----
    //
    // §15(3)(a) CGST: a discount given at or before the time of supply and
    // recorded in the invoice is excluded from the value of supply. So the
    // discount comes off HERE, before pass 4 taxes the result — never off the
    // grand total afterwards, which would overstate output tax on every
    // discounted bill.
    //
    // On a document with no discounts `lineDiscount` is 0 throughout and this is
    // the arithmetic the engine has always done.
    const grossByKey = new Map();
    const lineDiscountByKey = new Map();
    const taxableByKey = new Map();
    const billableKeys = [];
    let grossSubtotalPaise = 0;
    let lineDiscountTotalPaise = 0;
    for (const line of lines) {
        if (line.isGroupHeader === true) {
            continue;
        }
        const gross = taxableFor(line);
        const lineDiscount = line.discountPaise ?? 0;
        if (lineDiscount > gross) {
            throw new DiscountExceedsValueError({
                scope: "line",
                key: line.key,
                discountPaise: lineDiscount,
                availablePaise: gross,
                message: `Line "${line.key}" has a discount of ${lineDiscount} paise but is only worth ${gross} paise — a discount may not exceed what it discounts`,
            });
        }
        grossByKey.set(line.key, gross);
        lineDiscountByKey.set(line.key, lineDiscount);
        taxableByKey.set(line.key, gross - lineDiscount);
        billableKeys.push(line.key);
        grossSubtotalPaise += gross;
        lineDiscountTotalPaise += lineDiscount;
    }
    // ---- pass 3b: apportion the whole-document discount, if there is one -----
    //
    // Skipped entirely when absent, so an undiscounted document runs exactly the
    // path it ran before this feature existed.
    //
    // Per-line discounts have already been taken above, so the weights here are
    // what REMAINS on each line — which is what makes rule "per-line first, then
    // the whole-invoice one over what is left" fall out of the ordering rather
    // than needing special handling.
    const apportionedByKey = new Map();
    if (documentDiscountPaise > 0) {
        const weights = billableKeys.map((key) => taxableByKey.get(key) ?? 0);
        let remainingTotal = 0;
        for (const weight of weights) {
            remainingTotal += weight;
        }
        if (documentDiscountPaise > remainingTotal) {
            throw new DiscountExceedsValueError({
                scope: "document",
                key: null,
                discountPaise: documentDiscountPaise,
                availablePaise: remainingTotal,
                message: `Document discount of ${documentDiscountPaise} paise exceeds the ${remainingTotal} paise remaining after line discounts — a discount may not exceed what it discounts`,
            });
        }
        const shares = apportionByWeights(documentDiscountPaise, weights);
        for (let index = 0; index < billableKeys.length; index += 1) {
            const key = billableKeys[index];
            const share = shares[index];
            apportionedByKey.set(key, share);
            // apportionByWeights guarantees share <= weight, so this cannot go
            // negative — it asserts that itself rather than trusting the caller.
            taxableByKey.set(key, (taxableByKey.get(key) ?? 0) - share);
        }
    }
    // ---- pass 3c: accumulate each header's children, from the FINAL values ---
    //
    // A group header's printed RATE is the sum of its children's taxable amounts
    // (spec §8.1) — derived, never typed. It must be summed from the discounted
    // figures, otherwise a header prints a gross total above children that print
    // net, which is precisely the stale-parent-rate defect that §8.1 and golden
    // invoice 26-27/66 exist to eliminate, re-created by the discount feature.
    //
    // There is exactly ONE place this sum is computed, and it reads the final
    // map, so the two cannot drift apart. With no discount the final map equals
    // the gross map and the header rate is unchanged to the paise.
    const childTaxableByParent = new Map();
    for (const line of lines) {
        if (line.isGroupHeader === true) {
            continue;
        }
        const parentKey = line.parentKey ?? null;
        if (parentKey !== null) {
            childTaxableByParent.set(parentKey, (childTaxableByParent.get(parentKey) ?? 0) + (taxableByKey.get(line.key) ?? 0));
        }
    }
    // ---- pass 4: taxes, in the caller's original line order -----------------
    const computed = [];
    let subtotalPaise = 0;
    let cgstPaise = 0;
    let sgstPaise = 0;
    let igstPaise = 0;
    for (const line of lines) {
        if (line.isGroupHeader === true) {
            // A group header contributes NOTHING to the totals (spec §3). Its printed
            // rate is derived from its children — never typed (spec §8.1).
            computed.push({
                key: line.key,
                isGroupHeader: true,
                grossTaxablePaise: 0,
                lineDiscountPaise: 0,
                apportionedDiscountPaise: 0,
                taxablePaise: 0,
                cgstPaise: 0,
                sgstPaise: 0,
                igstPaise: 0,
                lineTotalPaise: 0,
                displayRatePaise: childTaxableByParent.get(line.key) ?? 0,
            });
            continue;
        }
        const taxablePaise = taxableByKey.get(line.key) ?? 0;
        // Belt and braces: nothing downstream — tax, subtotal, the printed amount —
        // has any meaning for a negative value of supply, and a sign flip here
        // would propagate silently onto a legal document.
        assertNonNegativeInteger(taxablePaise, `Line "${line.key}" taxablePaise`);
        let lineCgst = 0;
        let lineSgst = 0;
        let lineIgst = 0;
        if (supplyType === "INTRA_STATE") {
            lineCgst = divRoundHalfUp(mulExact(taxablePaise, line.gstRateBp, `Line "${line.key}" CGST`), CGST_SGST_DIVISOR);
            lineSgst = lineCgst;
        }
        else {
            lineIgst = divRoundHalfUp(mulExact(taxablePaise, line.gstRateBp, `Line "${line.key}" IGST`), IGST_DIVISOR);
        }
        const lineTotalPaise = taxablePaise + lineCgst + lineSgst + lineIgst;
        computed.push({
            key: line.key,
            isGroupHeader: false,
            grossTaxablePaise: grossByKey.get(line.key) ?? 0,
            lineDiscountPaise: lineDiscountByKey.get(line.key) ?? 0,
            apportionedDiscountPaise: apportionedByKey.get(line.key) ?? 0,
            taxablePaise,
            cgstPaise: lineCgst,
            sgstPaise: lineSgst,
            igstPaise: lineIgst,
            lineTotalPaise,
            displayRatePaise: line.ratePaise,
        });
        subtotalPaise += taxablePaise;
        cgstPaise += lineCgst;
        sgstPaise += lineSgst;
        igstPaise += lineIgst;
    }
    assertInteger(subtotalPaise, "document subtotalPaise");
    const totalDiscountPaise = lineDiscountTotalPaise + documentDiscountPaise;
    // The whole point of apportioning rather than deducting: not one paisa of the
    // discount may go missing or be invented between the gross value and the
    // value of supply that gets taxed and printed.
    if (grossSubtotalPaise - totalDiscountPaise !== subtotalPaise) {
        throw new Error(`computeTotals: internal invariant broken — gross ${grossSubtotalPaise} less discount ${totalDiscountPaise} is not the taxable subtotal ${subtotalPaise}`);
    }
    const totalTaxPaise = cgstPaise + sgstPaise + igstPaise;
    const rawTotalPaise = subtotalPaise + totalTaxPaise;
    const grandTotalPaise = roundToNearestRupeePaise(rawTotalPaise);
    const roundOffPaise = grandTotalPaise - rawTotalPaise;
    const totals = {
        supplyType,
        grossSubtotalPaise,
        lineDiscountTotalPaise,
        documentDiscountPaise,
        totalDiscountPaise,
        subtotalPaise,
        cgstPaise,
        sgstPaise,
        igstPaise,
        totalTaxPaise,
        roundOffPaise,
        grandTotalPaise,
        amountInWords: amountInWords(grandTotalPaise),
    };
    return { lines: computed, totals };
}
//# sourceMappingURL=compute.js.map